// Pure formatting: LogEntry -> human-readable text. No storage / no side effects.
import type { LogEntry } from './types';
import type { SystemInfo } from './system-info';

export interface FileMeta {
  generatedAt: number;
  env: string;
  appVersion?: string;
  userEmail?: string;
  userAgent: string;
  system?: SystemInfo;
}

function pad(n: number, width = 2): string {
  return `${n}`.padStart(width, '0');
}

function timeOfDay(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

export function formatLine(entry: LogEntry): string {
  const level = entry.level.toUpperCase().padEnd(5);
  const category = entry.category.padEnd(8);
  let line = `[${timeOfDay(entry.ts)}] ${level} ${category} ${entry.message}`;
  if (entry.data !== undefined) {
    line += `\n    ${safeJson(entry.data)}`;
  }
  return line;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
}

// Human-readable system block for the file header. The same data is also
// emitted as the structured `app` "session info" entry in the body (which the
// analyze-logs parser reads), so this block is purely for people opening the
// raw .log. Each line is omitted entirely when it carries no information.
function systemLines(s: SystemInfo): string {
  const lines: string[] = [];

  const platform = [
    s.os && `OS: ${s.os}`,
    s.architecture && `arch ${s.architecture}`,
    s.deviceModel && `model ${s.deviceModel}`,
    s.mobile != null && `mobile ${s.mobile}`,
  ].filter(Boolean);
  const browser = [
    s.browser && `Browser: ${s.browser}`,
    s.engine && `Engine: ${s.engine}`,
  ].filter(Boolean);
  if (platform.length) lines.push(`# ${platform.join(' | ')}`);
  if (browser.length) lines.push(`# ${browser.join(' | ')}`);

  const hardware = [
    s.cpuCores != null && `${s.cpuCores} cores`,
    s.deviceMemoryGb != null && `${s.deviceMemoryGb} GB RAM`,
    s.screen && `screen ${s.screen}`,
    s.viewport && `viewport ${s.viewport}`,
    s.touchPoints != null && `touch ${s.touchPoints}`,
  ].filter(Boolean);
  if (hardware.length) lines.push(`# Hardware: ${hardware.join(' | ')}`);

  if (s.jsHeap) {
    lines.push(`# Memory (JS heap): used ${s.jsHeap.usedMb}MB / total ${s.jsHeap.totalMb}MB / limit ${s.jsHeap.limitMb}MB`);
  }

  const env = [
    s.language && `locale ${s.language}`,
    s.timezone && `TZ ${s.timezone}`,
    s.network && `net ${s.network}`,
    s.online != null && `online ${s.online}`,
    s.cookiesEnabled != null && `cookies ${s.cookiesEnabled}`,
  ].filter(Boolean);
  if (env.length) lines.push(`# Env: ${env.join(' | ')}`);

  return lines.length ? lines.join('\n') + '\n' : '';
}

export function buildFile(entries: LogEntry[], meta: FileMeta): string {
  const header =
    `# Songhunt logs — ${new Date(meta.generatedAt).toISOString()} | env: ${meta.env}` +
    `${meta.appVersion ? ` | version: ${meta.appVersion}` : ''}` +
    ` | user: ${meta.userEmail ?? 'anonymous'} | entries: ${entries.length}\n` +
    `# UA: ${meta.userAgent}\n` +
    (meta.system ? systemLines(meta.system) : '');
  const body = entries.map(formatLine).join('\n');
  return `${header}\n${body}\n`;
}
